/**
 * Global fetch interceptor for the cookie-based auth migration (Phase 2).
 *
 * Why monkey-patch window.fetch?
 *   The codebase has ~199 `fetch(...)` call sites across 51 files. Refactoring
 *   every one of them to pass `credentials: 'include'` would be a massive
 *   surface-area change with high regression risk. Instead, we install a
 *   one-time interceptor here that:
 *     1. Adds `credentials: 'include'` to every request that targets our
 *        backend (so the httpOnly auth cookie is sent automatically).
 *     2. Leaves everything else (third-party APIs, asset URLs, etc.) untouched.
 *
 * Side effects of this approach:
 *   - Existing `Authorization: Bearer <token>` headers continue to be sent,
 *     but the backend now reads the cookie first and ignores the header when
 *     the cookie is present. After Phase 2 lands the Bearer headers carry
 *     garbage (`Bearer null`) because we stop writing the token to
 *     localStorage — that's fine, the cookie path takes precedence.
 *   - Idempotent: the patch flips a flag the first time it runs so multiple
 *     imports (e.g., HMR) don't compound wrappers.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const _isBackendRequest = (input) => {
  if (!BACKEND_URL) return false;
  try {
    let url;
    if (typeof input === 'string') {
      url = input;
    } else if (input && typeof input.url === 'string') {
      url = input.url;
    } else {
      return false;
    }
    // Same-origin absolute or relative `/api/...` paths both qualify.
    return url.startsWith(BACKEND_URL) || url.startsWith('/api/');
  } catch {
    return false;
  }
};

const installAuthFetchInterceptor = () => {
  if (typeof window === 'undefined' || !window.fetch) return;
  if (window.__voxsmythAuthFetchPatched) return;
  window.__voxsmythAuthFetchPatched = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = (input, init = {}) => {
    if (_isBackendRequest(input)) {
      // Only add credentials when the caller hasn't explicitly opted out.
      // (`omit` is the only value that means "no cookies, ever".)
      if (init.credentials !== 'omit') {
        init = { ...init, credentials: 'include' };
      }
    }
    return originalFetch(input, init);
  };
};

installAuthFetchInterceptor();

export default installAuthFetchInterceptor;
